import type { PlatformV1CompleteFeishuLoginRequest } from '../apis/useAuthApi'
import type { V1User } from '../client'

import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { toast } from 'vue-sonner'

import { authServiceCompleteFeishuLogin, authServiceFeishuLogin } from '../apis/useAuthApi'
import { authServiceLogin, authServiceMe } from '../client'
import { useApi } from '../composables/useApi'
import { getCachedLocalStorage } from '../composables/useAppStorage'
import { regions } from '../constants/regions'
import { errorToMessage } from '../utils/error'
import { useRegionStore } from './region'

// Define user type for better type safety
export interface User extends V1User {
  avatar?: string
}

// Define login credentials interface
export interface LoginCredentials {
  username: string
  password: string
  rememberMe?: boolean
}

export interface FeishuLoginPayload {
  code: string
  redirectUri: string
}

function normalizeFeishuLoginResponse<T extends Record<string, any>>(data: T) {
  return {
    ...data,
    bindingCreated: data.bindingCreated ?? data.binding_created,
    requiresAccountSelection: data.requiresAccountSelection ?? data.requires_account_selection,
    pendingToken: data.pendingToken ?? data.pending_token,
    feishuUser: data.feishuUser ?? data.feishu_user,
    bindableUsers: data.bindableUsers ?? data.bindable_users,
  }
}

export const useUserStore = defineStore('user', () => {
  const regionStore = useRegionStore()

  // Region-specific storage keys
  const getStorageKey = (key: string) =>
    `user/${regionStore.currentRegionKey}/${key}`

  const storageToken = useLocalStorage(() => getStorageKey('token'), '')
  const storageUser = useLocalStorage(() => getStorageKey('info'), '', {
    serializer: {
      read: (v: string) => (v ? JSON.parse(v) : undefined),
      write: (v: any) => JSON.stringify(v),
    },
  })

  const user = ref<User | undefined>(storageUser.value)
  const isAuthenticated = computed(() => !!storageToken.value)

  // Check if user is authenticated in a specific region
  const isAuthenticatedInRegion = (regionKey: string) => {
    const tokenStorage = getCachedLocalStorage<string | null>(`user/${regionKey}/token`, null)
    return !!tokenStorage.value
  }

  // Clear all region data when switching regions
  const clearCurrentRegionData = () => {
    user.value = undefined
    storageToken.value = ''
    storageUser.value = ''
  }

  // Watch for region changes and clear user data only if not authenticated in target region
  watch(
    () => regionStore.currentRegionKey,
    (newRegionKey) => {
      // Only clear data if user is not authenticated in the new region
      if (!isAuthenticatedInRegion(newRegionKey)) {
        clearCurrentRegionData()
      }
    },
  )

  const login = async (credentials: LoginCredentials) => {
    try {
      const { data } = (await useApi(authServiceLogin, {
        body: {
          username: credentials.username,
          password: credentials.password,
        },
      })).expect('登录失败')

      const userData = data.user
      storageToken.value = data.token
      user.value = userData
      storageUser.value = userData

      return data
    }
    catch (error) {
      console.error(error)
      toast.error(errorToMessage(error))
      throw error
    }
  }

  const loginWithFeishu = async (payload: FeishuLoginPayload) => {
    try {
      const { data } = (await useApi(authServiceFeishuLogin, {
        body: {
          code: payload.code,
          redirectUri: payload.redirectUri,
        },
      })).expect('飞书登录失败')
      const normalizedData = normalizeFeishuLoginResponse(data)

      if (normalizedData.requiresAccountSelection)
        return normalizedData

      if (!normalizedData.token || !normalizedData.user) {
        throw new Error('飞书登录返回缺少登录凭证')
      }

      const userData = normalizedData.user
      storageToken.value = normalizedData.token
      user.value = userData
      storageUser.value = userData

      return normalizedData
    }
    catch (error) {
      console.error(error)
      toast.error(errorToMessage(error))
      throw error
    }
  }

  const completeFeishuLogin = async (payload: PlatformV1CompleteFeishuLoginRequest) => {
    try {
      const { data } = (await useApi(authServiceCompleteFeishuLogin, {
        body: payload,
      })).expect('飞书登录失败')

      const userData = data.user
      storageToken.value = data.token
      user.value = userData
      storageUser.value = userData

      return data
    }
    catch (error) {
      console.error(error)
      toast.error(errorToMessage(error))
      throw error
    }
  }

  const me = async () => {
    try {
      const { data } = (await useApi(authServiceMe, {})).expect('获取用户信息失败')
      const userData = data.user
      storageUser.value = userData
      user.value = userData
    }
    catch (error) {
      toast.error(errorToMessage(error))
      throw error
    }

    // Update avatar from gravatar (best-effort).
    // NOTE: `crypto.subtle` is unavailable on non-secure origins (e.g. http://LAN-IP),
    // so do not fail `me()` just because hashing is not supported.
    if (user.value) {
      try {
        const subtle = globalThis.crypto?.subtle
        if (!subtle?.digest) {
          user.value.avatar = 'https://www.gravatar.com/avatar/?d=identicon'
          return
        }

        const emailHash = Array.from(
          new Uint8Array(
            await subtle.digest('SHA-256', new TextEncoder().encode(user.value.email)),
          ),
        ).map(b => b.toString(16).padStart(2, '0')).join('')
        user.value.avatar = `https://www.gravatar.com/avatar/${emailHash}?d=identicon`
      }
      catch {
        user.value.avatar = 'https://www.gravatar.com/avatar/?d=identicon'
      }
    }
  }

  const logout = async (): Promise<void> => {
    try {
      // Clear state for current region
      user.value = undefined
      storageToken.value = ''
      storageUser.value = ''

      // Clear login state for all regions using cached instances
      regions.forEach((region) => {
        const tokenStorage = getCachedLocalStorage<string | null>(`user/${region.envKey}/token`, null)
        const infoStorage = getCachedLocalStorage<User | undefined>(`user/${region.envKey}/info`, undefined)
        tokenStorage.value = null
        infoStorage.value = undefined
      })
    }
    catch (error) {
      console.error('Logout error:', error)
    }
  }

  return {
    isAuthenticated,
    storageToken,
    user,

    login,
    loginWithFeishu,
    completeFeishuLogin,
    me,
    logout,
    clearCurrentRegionData,
    isAuthenticatedInRegion,
  }
})
